import { Notification } from 'element-ui'
import { webmanOnMessage, getPushConfig } from '@/utils/webman-push'
import { getChatLists, getChatRecord, updateUnread } from '@/api/chat/webman'

// 模块级 flag，防止重复注册（登录多次也只注册一份监听）
let started = false

/**
 * webman/push 全局消息中心
 * 对应 lvtongmeng-admin 的 src/stores/pushMessage.js
 */
const pushMessage = {
  namespaced: true,

  state: {
    /** 按事件名缓存最近一条数据 { [event]: data } */
    latest: {},
    /** 收到的消息数组（按时间顺序追加） */
    list: [],
    /** 咨询中列表（由在线咨询页加载，收到新消息时更新对应项的 content） */
    onList: [],
    /** 待接入列表（store 自己维护，收到 new_message 时自动刷新） */
    pendingList: [],
    pendingTotal: 0,
    pendingPage: 1,
    pendingLimit: 20,
    /** 搜索条件（待接入 / 咨询中 / 已结束 三组共用） */
    searchForm: { user_nickname: '', user_mobile: '' },
    /** 当前选中的会话 id（用于判断新消息是否已读） */
    currentChatId: null,
    /** 当前打开会话的消息列表（最新在底部；收到该会话新消息时自动刷新） */
    currentRecord: []
  },

  mutations: {
    SET_LATEST(state, { event, data }) {
      state.latest = { ...state.latest, [event]: data }
    },
    PUSH_LIST(state, data) {
      state.list.push(data)
    },
    SET_ON_LIST(state, list) {
      state.onList = list || []
    },
    SET_PENDING(state, { list, total, page }) {
      state.pendingList = list || []
      state.pendingTotal = total || 0
      if (page != null) state.pendingPage = page
    },
    SET_PENDING_PAGE(state, page) {
      state.pendingPage = page
    },
    SET_SEARCH_FORM(state, form) {
      state.searchForm = { ...state.searchForm, ...form }
    },
    SET_CURRENT_CHAT_ID(state, id) {
      state.currentChatId = id
      state.currentRecord = []
    },
    SET_CURRENT_RECORD(state, record) {
      state.currentRecord = record || []
    },
    /** 新消息合并进咨询中列表：删除旧记录 → 合并保留字段后插到最上面 */
    MERGE_ON_LIST(state, { id, data, unreadTotal }) {
      const idx = state.onList.findIndex(i => Number(i.id) === Number(id))
      if (idx === -1) return
      const old = state.onList[idx]
      const merged = { ...old, ...data }
      // 模板显示的是 chatRecord.content / create_time，这里同步"最后一条消息"
      merged.chatRecord = {
        ...(old.chatRecord || {}),
        content: data.content != null ? data.content : (old.chatRecord || {}).content,
        create_time: data.create_time != null ? data.create_time : (old.chatRecord || {}).create_time,
        type: data.type != null ? data.type : (old.chatRecord || {}).type
      }
      if (unreadTotal != null) merged.unread_total = unreadTotal
      state.onList.splice(idx, 1)
      state.onList.unshift(merged)
    },
    RESET(state) {
      state.latest = {}
      state.list = []
      state.onList = []
      state.pendingList = []
      state.pendingTotal = 0
      state.pendingPage = 1
      state.searchForm = { user_nickname: '', user_mobile: '' }
      state.currentChatId = null
      state.currentRecord = []
    }
  },

  actions: {
    /** 登录后调用一次，注册全局唯一监听 */
    startListening({ state, commit, dispatch }) {
      if (started) return
      // 频道由 /admin/im/wstoken 下发，登录时存进 pushConfig
      const config = getPushConfig()
      const channel = (config && config.channel) || ''
      if (!channel) {
        console.warn('🚀拿不到推送频道（pushConfig.channel 为空），无法订阅')
        return
      }
      started = true

      // 事件 message：咨询中的新消息 → 更新咨询中列表 + 弹通知
      webmanOnMessage(channel, 'message', (data) => {
        console.log('🚀收到咨询中消息', data)
        commit('SET_LATEST', { event: 'message', data })
        commit('PUSH_LIST', data)
        // 新消息匹配咨询中列表：conv_id ↔ onList 里的 id
        if (data.conv_id != null) {
          const old = state.onList.find(i => Number(i.id) === Number(data.conv_id))
          if (old) {
            // 未读：正在看这个会话 → 清零 + 调接口已读；没在看 → 本地 +1
            const isCurrent = Number(old.id) === Number(state.currentChatId)
            commit('MERGE_ON_LIST', {
              id: data.conv_id,
              data,
              unreadTotal: isCurrent ? 0 : (Number(old.unread_total) || 0) + 1
            })
            if (isCurrent) {
              updateUnread({ id: old.id, unread_total: 0 })
            }
          }
        }
        // 消息属于当前打开的会话 → 刷新聊天记录，让当前窗口同步
        if (Number(data.conv_id) === Number(state.currentChatId)) {
          dispatch('refreshCurrentRecord')
        }
        dispatch('_notify', data)
      })

      // 事件 new_message：待接入的新咨询 → 自动刷新待接入列表 + 弹通知
      webmanOnMessage(channel, 'new_message', (data) => {
        console.log('🚀收到待接入新咨询', data)
        commit('PUSH_LIST', data)
        // 待接入回第一页重新拉取（沿用当前搜索条件）
        dispatch('loadPending')
        dispatch('_notify', data)
      })
    },

    /** 待接入：拉第一页（reset 为 false 时沿用当前页码；搜索 / 新消息刷新用） */
    async loadPending({ state, commit }, reset = true) {
      if (reset) commit('SET_PENDING_PAGE', 1)
      const res = await getChatLists({
        page: state.pendingPage,
        limit: state.pendingLimit,
        status: 1,
        user_nickname: state.searchForm.user_nickname,
        user_mobile: state.searchForm.user_mobile
      })
      if (res.code == 1) {
        commit('SET_PENDING', {
          list: res.data.list || [],
          total: res.data.total || 0,
          page: state.pendingPage
        })
      }
    },

    /** 待接入：加载更多 */
    loadPendingMore({ state, commit, dispatch }) {
      commit('SET_PENDING_PAGE', state.pendingPage + 1)
      return dispatch('loadPending', false)
    },

    /** 收到消息时统一弹通知（全局只弹这一份） */
    _notify(context, data) {
      // data 结构以后端为准，这里防御性取值
      const title = (data && data.title) || '新消息'
      const message = (data && (data.content || data.message)) || '收到一条新的咨询消息'
      Notification({
        title,
        message,
        type: 'info',
        duration: 3000,
        onClick: () => {
          // 点击通知跳转到在线咨询页（延迟 require 规避 router ↔ store 循环依赖）
          const router = require('@/router').default
          if (router.currentRoute.path !== '/onlineConsult') {
            router.push('/onlineConsult')
          }
        }
      })
    },

    /** 咨询中列表加载后写入 store，供新消息匹配更新 */
    setOnList({ commit }, list) {
      commit('SET_ON_LIST', list)
    },

    /** 记录当前选中的会话 id（用于判断新消息是否已读），切换时清空上一会话的消息列表 */
    setCurrentChatId({ commit }, id) {
      commit('SET_CURRENT_CHAT_ID', id)
    },

    /** 拉取当前会话（或指定 id）的聊天记录，最新在最下面 */
    async refreshCurrentRecord({ state, commit }, id) {
      const chatId = id != null ? id : state.currentChatId
      if (chatId == null) return
      const res = await getChatRecord({ id: chatId, page: 1, limit: 20 })
      if (res.code == 1) {
        const list = res.data.list || res.data || []
        commit('SET_CURRENT_RECORD', [...list].reverse())
      }
    },

    /** 登出时调用：清状态 + 复位注册 flag，下次登录可重新注册 */
    stopListening({ commit }) {
      started = false
      commit('RESET')
    }
  }
}

export default pushMessage
